<template>
    <div class="mead-app-banner f fa-c sp-l-m sp-t-s sp-r-m sp-b-s"
        :role="uiRole"
        :class="[
            `mead-banner-style-${uiStyle}`,
            uiHasColorVars ? 'el--color' : ''
        ]"
        :style="uiColorVars">
        <!-- Icon -->
        <Icon :name="uiIcon" uiSize="l" class="sm-r-s" />
        <!-- Action Text -->
        <span class="sm-r-s">{{ label | athT }}</span>
        <!-- Action link -->
        <ButtonV2 v-if="uiShowLinkButton" :link="true" :label="linkText" @click="onLinkClick" />
        <!-- Spacer -->
        <div class="fg"></div>
        <!-- Right Side Button -->
        <ButtonV2 v-if="uiShowButton"
            :label="buttonText"
            :colorStyle="uiButtonStyle"
            :size="'s'"
            @click="onButtonClick" />
        <!-- Close action -->
        <ButtonIcon v-if="showClose"
            class="sm-l-s"
            :icon="'cross'"
            :transparent="true"
            @click="onClose" />
    </div>
</template>
<script>
import { isNeString } from '@aretheyhappy/utils';
import EnsureMeadInstall from '../../mixins/EnsureMeadInstall.js';
import ButtonIcon from '../atoms/ButtonIcon.vue';
import ButtonV2 from '../atoms/ButtonV2.vue';
import Icon from '../atoms/visuals/Icon.vue';

export default {
    name: 'mead-app-banner',
    mixins: [EnsureMeadInstall],
    props: {
        /**
         * Label text (i18n key) shown in the banner bar.
         */
        label: {type: String, default: '', required: true},
        /**
         * Banner style variant, controlling its background/foreground color and default icon
         * (see `BANNER_STYLES`, e.g. 'default', 'highlight', 'warning', 'info', 'danger').
         */
        bannerStyle: {type: String, default: () => MeadCommon.BANNER_STYLES.DEFAULT},
        /**
         * Color of the banner: a COLOR_STYLES value (e.g. "primary"), a tint of one (e.g. "warning-20")
         * or any CSS color (e.g. "#BAC3C4"). Overrides the colors from `bannerStyle`, which still sets
         * the icon and button.
         */
        color: {type: String, default: ''},
        /**
         * Overrides the icon that would otherwise be derived from `bannerStyle`.
         */
        overrideIcon: {type: String, default: ''},
        /**
         * Text for the inline link button shown after the label. When provided, the link button
         * is displayed and emits `onLinkClick` when clicked.
         */
        linkText: {type: String, default: ''},
        /**
         * Text for the button shown on the right side of the banner. When provided, the button is
         * displayed and emits `onButtonClick` when clicked.
         */
        buttonText: {type: String, default: ''},
        /**
         * Whether the close button should be shown. Emits `onClose` when clicked.
         */
        showClose: {type: Boolean, default: true}
    },
    components: {ButtonIcon, ButtonV2, Icon},
    methods: {
        onLinkClick: function (event) {
            this.$emit('onLinkClick', event);
        },
        onButtonClick: function (event) {
            this.$emit('onButtonClick', event);
        },
        onClose: function (event) {
            this.$emit('onClose', event);
        }
    },
    computed: {
        uiColorVars: function () {
            return MeadCommon.colorVars(this.color, {bg: '--banner-bg', fg: '--banner-fg'});
        },
        uiHasColorVars: function () {
            return Object.keys(this.uiColorVars).length > 0;
        },
        uiIcon: function () {
            if (isNeString(this.overrideIcon)) return this.overrideIcon;

            switch (this.uiStyle) {
                case MeadCommon.BANNER_STYLES.DANGER:
                    return 'prohibited';
                case MeadCommon.BANNER_STYLES.DEFAULT:
                    return 'bubble-dots';
                case MeadCommon.BANNER_STYLES.HIGHLIGHT:
                    return 'enter';
                case MeadCommon.BANNER_STYLES.INFO:
                    return 'graduation-hat';
                case MeadCommon.BANNER_STYLES.WARNING:
                    return 'warning'
                default:
                    return '';
            }
        },
        uiRole: function () {
            switch (this.uiStyle) {
                case MeadCommon.BANNER_STYLES.DANGER:
                case MeadCommon.BANNER_STYLES.WARNING:
                    return 'alert';
                case MeadCommon.BANNER_STYLES.INFO:
                case MeadCommon.BANNER_STYLES.DEFAULT:
                case MeadCommon.BANNER_STYLES.HIGHLIGHT:
                default:
                    return 'status';
            }
        },
        uiStyle: function () {
            if (MeadCommon.BANNER_STYLE_VALUES.includes(this.bannerStyle)) return this.bannerStyle;
            return MeadCommon.BANNER_STYLES.DEFAULT;
        },
        uiShowLinkButton: function () {
            return isNeString(this.linkText);
        },
        uiShowButton: function () {
            return isNeString(this.buttonText);
        },
        uiButtonStyle: function () {
            switch (this.uiStyle) {
                case MeadCommon.BANNER_STYLES.DANGER:
                case MeadCommon.BANNER_STYLES.INFO:
                case MeadCommon.BANNER_STYLES.DEFAULT:
                case MeadCommon.BANNER_STYLES.WARNING:
                    return MeadCommon.BUTTON_COLOR_STYLES.DARK;
                case MeadCommon.BANNER_STYLES.HIGHLIGHT:
                default:
                    return MeadCommon.BUTTON_COLOR_STYLES.PRIMARY;;
            }
        },
    }
}
</script>
<style lang="scss">
@import '../../../sass/_variables.scss';

.mead-app-banner {
    width: 100%;
    height: 5rem;
    font-size: $fs-s;
    font-family: $ff-b;

    @mixin bannerStyle ($name, $bg, $fg) {
        &.mead-banner-style-#{$name} {
            background-color: $bg;
            color: $fg;
        }
    }

    @include bannerStyle('default', var(--c-brand-default-bg), var(--c-brand-default-fg));
    @include bannerStyle('highlight', var(--c-brand-highlight-bg), var(--c-brand-highlight-fg));
    @include bannerStyle('warning', var(--c-brand-warning-bg), var(--c-brand-warning-fg));
    @include bannerStyle('info', var(--c-brand-info-bg), var(--c-brand-info-fg));
    @include bannerStyle('danger', var(--c-brand-danger-bg), var(--c-brand-danger-fg));

    // Colors from the `color` prop, set as --banner-bg/--banner-fg
    &.el--color {
        background-color: var(--banner-bg);
        color: var(--banner-fg, var(--c-txt));
    }
}
</style>